import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Check, XCircle, CalendarDays, ShieldCheck, Clock } from "lucide-react";
import { Saguaro } from "@/components/Saguaro";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiErrorDetail, money } from "@/lib/api";

export default function OfferAccept() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);
  const [signature, setSignature] = useState("");
  const [agreeOpen, setAgreeOpen] = useState(false);

  useEffect(() => {
    api.get(`/offers/${token}`)
      .then((r) => setOffer(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [token]);

  const currency = offer?.currency || "usd";

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/offers/${token}/book`, {
        origin_url: window.location.origin,
        agreement_accepted: agree,
        agreement_signature: signature.trim(),
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const unavailable = offer && ["accepted", "expired", "cancelled"].includes(offer.status);
  const cov = offer?.damage_coverage || "none";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full bg-card rounded-2xl shadow-sm p-8 md:p-10" data-testid="offer-accept-card">
        <Link to="/" className="flex items-center justify-center gap-2 font-serif text-2xl font-semibold text-foreground mb-8">
          <Saguaro className="h-7 w-7 text-primary" /> The East Valley
        </Link>

        {loading && <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading offer…</div>}

        {!loading && error && !offer && (
          <div className="text-center" data-testid="offer-error">
            <XCircle className="h-14 w-14 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{error}</p>
          </div>
        )}

        {!loading && offer && (
          <>
            <div className="text-center mb-6">
              <p className="text-primary font-medium text-xs uppercase tracking-widest mb-1">A special offer for you</p>
              <h1 className="font-serif text-2xl font-semibold text-foreground">Hi {offer.guest_name}!</h1>
            </div>

            {offer.note && <p className="text-sm text-foreground bg-accent/50 rounded-lg p-3 mb-4 italic">"{offer.note}"</p>}

            <div className="rounded-xl bg-accent/50 p-4 space-y-2 text-sm mb-5" data-testid="offer-breakdown">
              <div className="flex items-center gap-2 text-foreground mb-1"><CalendarDays className="h-4 w-4 text-primary" /> {offer.check_in} → {offer.check_out} · {offer.nights} night(s)</div>
              <div className="flex justify-between text-muted-foreground"><span>Guests</span><span className="text-foreground">{offer.guests}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Stay ({offer.nights} night{offer.nights === 1 ? "" : "s"})</span><span className="text-foreground">{money(offer.total_price, currency)}</span></div>
              {offer.cleaning_fee_enabled && offer.cleaning_fee > 0 && (
                <div className="flex justify-between text-muted-foreground"><span>Cleaning fee</span><span className="text-foreground">{money(offer.cleaning_fee, currency)}</span></div>
              )}
              {offer.tax_amount > 0 && (
                <div className="flex justify-between text-muted-foreground"><span>Lodging tax</span><span className="text-foreground">{money(offer.tax_amount, currency)}</span></div>
              )}
              {cov === "waiver" && (
                <div className="flex justify-between text-muted-foreground"><span>Damage waiver (non-refundable)</span><span className="text-foreground">{money(offer.damage_waiver_amount, currency)}</span></div>
              )}
              {cov === "deposit" && (
                <div className="flex justify-between text-muted-foreground"><span>Refundable security deposit</span><span className="text-foreground">{money(offer.damage_deposit_amount, currency)}</span></div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-border">
                <span className="text-foreground font-medium">Total</span>
                <span className="text-primary font-serif text-2xl font-semibold" data-testid="offer-total">{money(offer.grand_total, currency)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground pt-1"><span>Due now (50% deposit{cov === "waiver" ? " + waiver" : ""})</span><span className="text-foreground font-medium">{money(offer.deposit_amount, currency)}</span></div>
              <div className="flex justify-between text-xs text-muted-foreground"><span>Balance (due before check-in)</span><span>{money(offer.balance_due, currency)}</span></div>
            </div>

            {cov !== "none" && (
              <div className="flex items-start gap-2 text-xs text-muted-foreground bg-secondary/10 rounded-lg p-3 mb-5" data-testid="offer-damage-info">
                <ShieldCheck className="h-4 w-4 text-secondary flex-shrink-0 mt-0.5" />
                <span>
                  {cov === "waiver"
                    ? "This stay includes a non-refundable damage waiver — no large hold on your card."
                    : "This stay uses a refundable security deposit, collected with your balance and returned after checkout if there's no damage."}
                </span>
              </div>
            )}

            {unavailable ? (
              <div className="text-center py-4" data-testid="offer-unavailable">
                <XCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="font-medium text-foreground capitalize">This offer is {offer.status}.</p>
                <p className="text-sm text-muted-foreground mt-1">Please contact us if you'd still like to book these dates.</p>
              </div>
            ) : (
              <>
                {error && <p className="text-sm text-destructive text-center mb-3" data-testid="offer-book-error">{error}</p>}
                <div className="rounded-lg border border-border bg-accent/30 p-3 space-y-3 mb-4 text-left">
                  <label className="flex items-start gap-2.5 cursor-pointer" data-testid="offer-agreement-label">
                    <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" data-testid="offer-agreement-checkbox" />
                    <span className="text-xs text-foreground leading-relaxed">
                      I have read and agree to the{" "}
                      <button type="button" className="text-primary underline font-medium" data-testid="offer-view-agreement-btn" onClick={() => setAgreeOpen(true)}>Rental Agreement</button>.
                    </span>
                  </label>
                  <div>
                    <Label htmlFor="o-sign" className="text-xs">Type your full name to sign</Label>
                    <Input id="o-sign" data-testid="offer-agreement-signature" value={signature} onChange={(e) => setSignature(e.target.value)} className="mt-1.5 rounded-lg" placeholder="Full legal name" autoComplete="name" />
                  </div>
                </div>
                <Button className="w-full rounded-full bg-primary h-12 text-base" disabled={busy || !agree || !signature.trim()} data-testid="offer-accept-btn" onClick={accept}>
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Check className="h-5 w-5 mr-1" /> Accept &amp; pay {money(offer.deposit_amount, currency)} deposit</>}
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-3 flex items-center justify-center gap-1"><Clock className="h-3 w-3" /> Offer expires {(offer.expires_at || "").slice(0, 10)}</p>
              </>
            )}
          </>
        )}
      </div>

      <Dialog open={agreeOpen} onOpenChange={setAgreeOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="offer-agreement-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Rental Agreement</DialogTitle>
            <DialogDescription>Please read the full agreement below before you sign.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto whitespace-pre-line text-sm text-foreground leading-relaxed pr-2" data-testid="offer-agreement-text">
            {offer?.rental_agreement || "The rental agreement will be provided at booking."}
          </div>
          <Button className="w-full rounded-full bg-primary h-11 mt-2" data-testid="offer-agreement-accept-btn" onClick={() => { setAgree(true); setAgreeOpen(false); }}>
            I have read and agree
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
